'use client';

import SocialMedia from '@/basket/components/thank-you-page/social-media/social-media';
import Button from '@/components/base/button/button';
import Divider from '@/components/base/divider/divider';
import PhosphorIcon from '@/components/base/icon/phosphor-icon';
import { useSiteId } from '@/contexts/domain/hooks';
import type { FooterResponse } from '@/features/footer/api/types';
import { Link, usePathname } from '@/navigation';
import { LanguageChange } from '@/navigation/components/information-section/language-change';
import { ACCOUNT_ROUTES, BASKET_ROUTES } from '@/routes';
import { ArrowUp, EnvelopeSimple, Phone } from '@phosphor-icons/react';
import { clsx } from 'clsx';
import { useTranslations } from 'next-intl';
import type { LinkProps } from 'next/link';
import { SimplifyFooter } from './simplify-footer';
type LinkProp = Omit<LinkProps, 'locale'> & {
  title: string;
};

type Props = {
  isSubscribedToNewsletter: boolean;
  footerData: FooterResponse;
};

const simplifyPathnames = [
  BASKET_ROUTES.LOGIN,
  BASKET_ROUTES.LOGIN_EMAIL,
  BASKET_ROUTES.LOGIN_GUEST,
  BASKET_ROUTES.CHECKOUT,
] as string[];

const Footer = ({ isSubscribedToNewsletter, footerData }: Props) => {
  const t = useTranslations('common');
  const tn = useTranslations('navigation');
  const pathname = usePathname();
  const isSimplify = simplifyPathnames.includes(pathname ?? '');

  const siteId = useSiteId();
  const showLanguageChange = siteId === 'kg_ee' || siteId === 'ds_lv';
  const handleToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const legalLinks = [
    ...(footerData?.policiesLinks ?? []),
    {
      title: tn('cookie_settings'),
      href: ACCOUNT_ROUTES.COMMUNICATION,
    },
  ];

  if (isSimplify) {
    return <SimplifyFooter />;
  }

  return (
    <>
      <div className="mt-xxl bg-alt py-l">
        <div className="container flex flex-col xl:flex-row xl:justify-between">
          <div
            className={clsx(
              'mb-m flex xs:flex-row flex-col items-center justify-center gap-m xl:order-2 xl:items-start',
              showLanguageChange &&
                'xs:justify-between xl:flex-col xl:items-end',
            )}
          >
            <Button
              variant="tertiary"
              size="small"
              className={clsx(
                'flex w-fit items-center',
                showLanguageChange && 'xs:order-2 xl:order-1',
              )}
              onClick={handleToTop}
            >
              <PhosphorIcon Icon={ArrowUp} className="icon-s" />
              {t('go_to_top')}
            </Button>
            {showLanguageChange && <LanguageChange />}
          </div>
          <div className="l:mb-l flex w-full l:flex-row flex-col l:justify-between xl:order-1 xl:w-10/12 xl:gap-l">
            <div className="col-span-5 flex h-full l:w-3/10 w-full flex-col gap-xxs rounded-m border border-decorative bg-main p-inner s:py-s">
              <div className="flex flex-col max-l:items-center">
                <div className="flex items-center gap-xxs max-l:justify-center">
                  {footerData.contactInfo.phones.map(({ title, href }) => (
                    <a
                      key={href}
                      href={href}
                      className="flex min-h-clickable-minimum items-center gap-xxs underline decoration-decorative underline-offset-4"
                    >
                      <PhosphorIcon Icon={Phone} className="icon-s" />
                      {title}
                    </a>
                  ))}
                  {/*<div className="flex min-h-clickable-minimum items-center">*/}
                  {/*  or*/}
                  {/*</div>*/}
                  {/*<button className="flex min-h-clickable-minimum items-center gap-xxs underline decoration-decorative underline-offset-4">*/}
                  {/*  <PhosphorIcon Icon={ChatCenteredText} className="icon-s" />*/}
                  {/*  {tn('chat')}*/}
                  {/*</button>*/}
                </div>
                {footerData.contactInfo.openingHours?.map(
                  ({ range, hours }) => (
                    <span className="text-muted" key={range + hours}>
                      {range}: {hours}
                    </span>
                  ),
                )}
              </div>
              <div className="flex flex-col max-l:items-center">
                {footerData.contactInfo.emails.map(({ title, href }) => (
                  <a
                    href={href}
                    key={title}
                    className="flex min-h-clickable-minimum items-center gap-xxs underline decoration-decorative underline-offset-4"
                  >
                    <PhosphorIcon Icon={EnvelopeSimple} className="icon-s" />
                    {title}
                  </a>
                ))}
              </div>
            </div>
            <div className="l:my-0 my-l s:flex-row justify-between gap-l">
              <div className="grid-12 w-full gap-xs">
                <ul className="col-span-6 s:col-span-4 flex flex-col">
                  {footerData.footerLinks.slice(0, 6).map((link) => (
                    <LinkFooter key={link.title} {...link} />
                  ))}
                </ul>
                <ul className="col-span-6 s:col-span-4 flex flex-col">
                  {footerData.footerLinks.slice(6).map((link) => (
                    <LinkFooter key={link.title} {...link} />
                  ))}
                </ul>
                {footerData.groupLinks.length > 0 && (
                  <div className="col-span-full s:col-span-4 flex flex-col max-s:mt-m">
                    <div className="text-body-bold">{t('our_group')}:</div>
                    <ul>
                      {footerData.groupLinks.map((link) => (
                        <LinkFooter key={link.title} {...link} />
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Divider />
        <div
          className={clsx(
            'container mt-l flex l:flex-row flex-col l:items-center l:justify-center',
          )}
        >
          <SocialMedia
            showTitle={false}
            footerData={footerData}
            classNames={clsx(
              isSubscribedToNewsletter && 'mt-0',
              's:flex-row s:items-center s:gap-l no-flex-gap:s:space-x-l',
            )}
            sectionClassNames="my-0"
          />
        </div>
      </div>
      <div className="bg-contrast text-inverted">
        <div className="h-xxs max-h-xxs min-h-xxs w-full bg-brand" />
        <div className="container">
          <div className="flex l:flex-row flex-col l:items-center justify-center l:justify-between gap-xs s:gap-m xs:gap-s l:space-y-0 no-flex-gap:s:space-y-m no-flex-gap:space-y-xs no-flex-gap:xs:space-y-s s:py-m">
            <div className="flex w-fit items-center justify-items-center gap-m gap-y-xxs no-flex-gap:space-x-m max-l:mx-auto">
              {legalLinks.map(({ title, href }) => (
                <Link
                  className="w-fit text-small underline"
                  key={title}
                  rel="follow"
                  href={href}
                >
                  {title}
                </Link>
              ))}
            </div>
            <div className="text-center text-small">
              © 2006–{new Date().getFullYear()} {tn('copyrights.copyright')}{' '}
              &nbsp;
              {footerData.contactInfo.siteName}{' '}
              {tn('copyrights.rights_reserved')}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const LinkFooter = ({
  newWindow,
  href,
  title,
}: LinkProp & { title: string; newWindow: number }) => {
  return (
    <li>
      <Link href={href} target={newWindow ? '_blank' : '_self'} rel="follow">
        {title}
      </Link>
    </li>
  );
};

export default Footer;
